import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';
import { AnalyticsController } from '../../layout/Analytics';
import { useLayoutContext } from '../../providers/LayoutProvider';
import './VOBTimeline.scss';

interface Slide {
    title: string;
    text?: string;
    imageLg?: any;
    imageLgAlt?: string;

    imageSm?: any;
    imageSmAlt?: string;
}

interface Year {
    year: string;
    cost: string;
    speed: string;
}

const HEIGHT_RATIO = 4;
export const VOBTimeline = ({ node }) => {
    const { headerRef, sitesNavRef } = useLayoutContext();
    const slides: Slide[] = node.relationships.field_slides.map((slide) => ({
        title: slide.field_title,
        text: slide.field_text_content
            ? slide.field_text_content.value
            : undefined,
        imageLg: slide.relationships.field_single_image
            ? slide.relationships.field_single_image.localFile
            : undefined,
        imageLgAlt: slide.field_single_image?.alt,
        imageSm: slide.relationships.field_image_mobile
            ? slide.relationships.field_image_mobile.localFile
            : undefined,
        imageSmAlt: slide.field_image_mobile?.alt,
    }));

    const years: Year[] = node.relationships.field_years.map((year) => ({
        year: year.field_year,
        speed: year.field_internet_speed,
        cost: year.field_cost_per_mbps,
    }));

    const [offset, setOffset] = useState(-999);
    const [bottom, setBottom] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [usableHeight, setUsableHeight] = useState(0);
    const [currentYear, setCurrentYear] = useState(years[0]);

    const inputRef = useRef() as any;

    useEffect(() => {
        const onScroll = () => {
            const { offsetTop, offsetHeight } = inputRef.current;
            const newOffset =
                window.scrollY - offsetTop - sitesNavRef?.current?.offsetHeight;
            setOffset(newOffset);

            const bottomOfWindow =
                window.innerHeight +
                window.scrollY -
                sitesNavRef?.current?.offsetHeight -
                headerRef?.current?.offsetHeight;
            const bottomOfElem = offsetTop + offsetHeight;
            setBottom(bottomOfWindow - bottomOfElem >= 0);

            const newCurrentSlide =
                ((slides.length + 0.5) / slides.length / HEIGHT_RATIO) *
                (newOffset / window.innerHeight);

            const clampedCurrentSlide = Math.min(
                Math.max(newCurrentSlide, 0),
                slides.length - 1
            );

            setCurrentSlide(clampedCurrentSlide);

            setCurrentYear(
                years[
                    Math.min(
                        Math.floor(
                            (clampedCurrentSlide / 2) * (years.length - 1)
                        ),
                        years.length - 1
                    )
                ]
            );
        };
        window.addEventListener(`scroll`, onScroll, { passive: false });
        return () => window.removeEventListener(`scroll`, onScroll);
    }, [headerRef, sitesNavRef]);

    useEffect(() => {
        setUsableHeight(window.innerHeight - headerRef?.current?.offsetHeight);
    }, [headerRef]);

    useEffect(() => {
        if (currentSlide === 1) {
            AnalyticsController.track({
                event: `broadband horizontal scroll slide 2`,
            });
        }
        if (currentSlide === 2) {
            AnalyticsController.track({
                event: `broadband horizontal scroll end`,
            });
        }
    }, [currentSlide]);

    return (
        <article className="vob-timeline" ref={inputRef}>
            <div
                className={`holder ${!bottom && offset >= 0 ? `sticky` : ``} ${
                    bottom ? `bottom` : ``
                }`}
                style={{
                    height: `${usableHeight}px`,
                }}
            >
                <div
                    className="slides"
                    style={{
                        left: `${Math.floor(currentSlide) * -100}vw`,
                    }}
                >
                    {slides.map((slide, idx) => (
                        <section key={idx}>
                            <div className="slide kite-container">
                                <div className="text">
                                    <h2>{slide.title}</h2>
                                    {!!slide.text && (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: slide.text,
                                            }}
                                        ></div>
                                    )}
                                </div>
                                {slide.imageLg && (
                                    <GatsbyImage
                                        image={getImage(slide.imageLg)}
                                        alt={slide.imageLgAlt}
                                        className={
                                            slide.imageSm ? `has-small` : ``
                                        }
                                        imgStyle={{ objectFit: `contain` }}
                                    />
                                )}
                                {slide.imageSm && (
                                    <GatsbyImage
                                        image={getImage(slide.imageSm)}
                                        alt={slide.imageSmAlt}
                                        className="small"
                                        imgStyle={{ objectFit: `contain` }}
                                    />
                                )}
                            </div>
                        </section>
                    ))}
                </div>
                <div
                    className="years"
                    style={{
                        left: `calc(50vw - 6.5rem - ${
                            years.indexOf(
                                years.find(
                                    (year) => year.year === currentYear?.year
                                )
                            ) * 12.1
                        }rem - ${Math.min(
                            Math.max(
                                0,
                                (currentSlide - 2) / (slides.length - 3)
                            ) * 100,
                            52
                        )}vw - ${
                            Math.floor(currentSlide) === slides.length - 1
                                ? 98
                                : 0
                        }vw)`,
                    }}
                >
                    <ul>
                        {years.map((year, idx) => (
                            <li
                                key={idx}
                                className={
                                    currentYear &&
                                    currentYear?.year === year.year
                                        ? `current`
                                        : undefined
                                }
                            >
                                <p className='typestack-title-6' >{year.year}</p>
                            </li>
                        ))}
                        <li className="gap"></li>
                    </ul>
                    <div className="endcap"></div>
                </div>
                <div
                    className={`year-details ${
                        currentSlide > 2.5 ? `hidden` : ``
                    }`}
                >
                    <h5>
                        {currentYear?.cost}
                        <small>Cost Per Mbps</small>
                    </h5>
                    <h5>
                        {currentYear?.speed}
                        <small>Internet Speed</small>
                    </h5>
                </div>
            </div>
            {slides.map((slide, idx) => (
                <div
                    key={idx}
                    className="dm"
                    style={{ height: `${usableHeight * HEIGHT_RATIO}px` }}
                ></div>
            ))}
        </article>
    );
};
